import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import UserMenu from "../components/UserMenu";
import StartBuildingButton, { BuildASkillButton } from "../components/StartBuildingButton";
import { useAuth } from "../contexts/AuthContext";

/**
 * /why-skills — the brand manifesto, promoted from the modal that used
 * to live in WhySkillsHover.jsx. Copy is verbatim from the modal —
 * no re-authoring per founder rule (Jul 12 2026). Three short paragraphs
 * plus the mint "until now." emphasis.
 */

const WhySkillsPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="why-skills-page">
      {/* Top bar — matches Skills/ThePath page chrome */}
      <header className="border-b border-[#4a6984]/10 bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="why-skills-home-link"
            className="inline-flex items-center gap-2 text-[#1c2d3d] font-black"
          >
            <span className="inline-block h-7 w-7 rounded-md bg-[#1c2d3d] text-white grid place-items-center font-black text-sm">
              A
            </span>
            Atomic Edu
          </Link>
          <div className="flex items-center gap-2">
            {user ? <UserMenu /> : <><BuildASkillButton /><StartBuildingButton /></>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-14 pb-24">
        <Link
          to="/"
          data-testid="why-skills-back"
          className="inline-flex items-center gap-1.5 text-[#4a6984] text-sm font-bold hover:text-[#1c2d3d] transition-colors mb-8"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Atomic Edu
        </Link>

        <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#96ded1]">
          Why Skills?
        </p>
        <h1 className="mt-2 font-black text-4xl sm:text-5xl text-[#1c2d3d] tracking-tight">
          The manifesto.
        </h1>

        <div className="mt-10 space-y-6 text-[17px] leading-[1.7] font-medium text-[#1c2d3d]">
          <p data-testid="why-skills-p1">
            The majority of hiring failures have nothing to do with technical
            incompetence. Employers aren&apos;t turning people away because they
            can&apos;t do the job. They&apos;re turning people away because they
            can&apos;t think through a problem, work inside a team, or recover
            when something goes sideways.
          </p>
          <p data-testid="why-skills-p2">
            Those are transferable skills. And they&apos;re built — or not built —
            in Grades 4 to 8.
          </p>
          <p data-testid="why-skills-p3">
            Ontario&apos;s curriculum names them. The Ministry believes in them.
            But no one has ever given teachers the tools to actually teach them —{" "}
            <strong
              data-testid="why-skills-emphasis"
              className="font-extrabold text-[#96ded1]"
            >
              until now.
            </strong>
          </p>
        </div>

        {/* Signposts to the rest of the manifesto surface — mirrors the
            four buttons on the new homepage section, minus "Why skills"
            itself. */}
        <div className="mt-14 grid gap-3 sm:grid-cols-3">
          <Link
            to="/skills"
            data-testid="why-skills-cta-library"
            className="ae-card p-4 border border-[#4a6984]/15 hover:border-[#1c2d3d]/40 hover:shadow-md transition-all text-[#1c2d3d]"
          >
            <p className="font-black text-sm">The Skills Library →</p>
            <p className="mt-1 text-xs text-[#4a6984] leading-snug">
              Playbooks for all eight skills, ready to teach.
            </p>
          </Link>
          <Link
            to="/the-path"
            data-testid="why-skills-cta-path"
            className="ae-card p-4 border border-[#4a6984]/15 hover:border-[#1c2d3d]/40 hover:shadow-md transition-all text-[#1c2d3d]"
          >
            <p className="font-black text-sm">The Path →</p>
            <p className="mt-1 text-xs text-[#4a6984] leading-snug">
              From your classroom to their future.
            </p>
          </Link>
          <Link
            to="/posters"
            data-testid="why-skills-cta-posters"
            className="ae-card p-4 border border-[#4a6984]/15 hover:border-[#1c2d3d]/40 hover:shadow-md transition-all text-[#1c2d3d]"
          >
            <p className="font-black text-sm">Free posters →</p>
            <p className="mt-1 text-xs text-[#4a6984] leading-snug">
              Seven transferable-skill posters for the classroom wall.
            </p>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default WhySkillsPage;
